import type { ChatTranscriptDimensions } from "@qw/chat";
import {
  ErrorSeverity,
  registerSuperProperties,
  UserFacingErrorKind,
  UserFacingErrorSurface,
} from "@third-plane/posthog-integration-interface";

import { captureUserFacingError, safePostHogCall } from "./posthog";

/**
 * QWIB's gray-screen instrumentation (B-2020).
 *
 * Three readings share `user_facing_error`, told apart by `operation`:
 *
 * - `root_render` — React tore the whole tree down. Without the root boundary this is the gray
 *   viewport itself; with it, it is a recoverable fallback plus this event.
 * - `main_thread_stall` — the renderer froze long enough for the app to look dead without being
 *   dead. B-2012's replay showed exactly this shape (rapid window switching over a very large
 *   tool-heavy transcript), and it is the reading we could not tell apart from a crash.
 * - `long_animation_frame` — the same freeze, with the attribution a `longtask` entry structurally
 *   cannot carry (B-2041). A `longtask` duration is wall clock: a renderer suspended under memory
 *   pressure reports the same enormous number as a runaway loop. Naming the frame's scripts, and
 *   how much of its duration they account for, is what tells those two apart.
 *
 * A stall is not a defect, so it ships at `warning` severity and stays out of any error rate. The
 * taxonomy in `@third-plane/posthog-integration-interface` is closed and owned elsewhere; it has no
 * performance event, and `user_facing_error` — "a user sees a recoverable or blocking application
 * error" — is the honest fit for a frozen renderer until one exists.
 *
 * Nothing here carries free text. `error.message` never ships: a render crash's message routinely
 * quotes the props that produced it, which on this surface means insured names and email bodies.
 * Transcript size rides along as super properties (see `recordQwibTranscriptDimensions`) rather than
 * as event properties, which is what correlates a crash or a stall with how much the renderer was
 * holding.
 */

/** Distinguishes the readings on the shared event. */
export const QwibTelemetryOperation = {
  RootRender: "root_render",
  MainThreadStall: "main_thread_stall",
  LongAnimationFrame: "long_animation_frame",
} as const;

/**
 * Deep stacks add noise, not signal — the frames nearest the throw are the ones that name the
 * component. Both bounds also keep a pathological stack from shipping a multi-kilobyte string.
 */
const MAX_COMPONENT_STACK_FRAMES = 12;
const MAX_COMPONENT_STACK_CHARS = 256;

/**
 * Only the built-in error names are reported as-is. A custom `Error` subclass can be named after
 * whatever it carries, and a name is the one part of an error we can ship — so anything outside the
 * language's own set collapses to a single bucket rather than being trusted.
 */
const STANDARD_ERROR_NAMES = new Set([
  "AggregateError",
  "Error",
  "EvalError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TypeError",
  "URIError",
]);
const NON_STANDARD_ERROR_CODE = "non_standard_error";
const NON_ERROR_CODE = "non_error_thrown";

const EMIT_FAILED = "[qwib telemetry] user_facing_error emit failed";
const LONG_FRAME_REGISTER_FAILED = "[qwib telemetry] long animation frame register failed";

const routeName = () => (typeof window === "undefined" ? "" : window.location.pathname);

/** `at Foo (…)` and `in Foo (at …)` are both shapes React has emitted; take the identifier from either. */
const COMPONENT_STACK_FRAME = /^\s*(?:at|in)\s+([^\s(]+)/;
const COMPONENT_IDENTIFIER = /^[A-Za-z_$][\w$.]*$/;

/**
 * Reduce a React component stack to the component identifiers nearest the throw. Identifiers are
 * code, not data; the file paths and URLs alongside them are dropped rather than trusted, since a
 * dev-server frame carries query strings and a bundled one carries nothing worth reading.
 */
export function sanitizeComponentStack(componentStack: string | null | undefined): string | undefined {
  if (!componentStack) {
    return undefined;
  }

  const frames: string[] = [];
  for (const line of componentStack.split("\n")) {
    const identifier = COMPONENT_STACK_FRAME.exec(line)?.[1];
    if (identifier && COMPONENT_IDENTIFIER.test(identifier)) {
      frames.push(identifier);
    }
    if (frames.length === MAX_COMPONENT_STACK_FRAMES) {
      break;
    }
  }

  return frames.length > 0 ? frames.join(">").slice(0, MAX_COMPONENT_STACK_CHARS) : undefined;
}

/** The whole of what a thrown value contributes: which built-in error it was, or that it wasn't one. */
export function categorizeError(error: unknown): string {
  if (!(error instanceof Error)) {
    return NON_ERROR_CODE;
  }

  return STANDARD_ERROR_NAMES.has(error.name) ? error.name : NON_STANDARD_ERROR_CODE;
}

/** A fatal render caught by the root boundary. Recoverable only because the fallback offers Reload. */
export function captureRootRenderCrash(error: unknown, componentStack: string | null | undefined): void {
  captureUserFacingError(EMIT_FAILED, {
    routeName: routeName(),
    surface: UserFacingErrorSurface.Unknown,
    errorKind: UserFacingErrorKind.Unknown,
    severity: ErrorSeverity.Error,
    component: sanitizeComponentStack(componentStack),
    errorCode: categorizeError(error),
    operation: QwibTelemetryOperation.RootRender,
    recoverable: true,
  });
}

/** The main thread was blocked long enough that the app looked dead. Not a failure — see the header. */
export function captureMainThreadStall(durationMs: number): void {
  captureUserFacingError(EMIT_FAILED, {
    routeName: routeName(),
    surface: UserFacingErrorSurface.Unknown,
    errorKind: UserFacingErrorKind.Timeout,
    severity: ErrorSeverity.Warning,
    component: "qwib-performance",
    operation: QwibTelemetryOperation.MainThreadStall,
    recoverable: true,
    durationMs: Math.round(durationMs),
  });
}

/**
 * The `long-animation-frame` fields this module reads. TypeScript's DOM lib has no definition for
 * either shape as of 5.9, so both are declared here rather than asserted at the call site.
 */
export type LongAnimationFrameScript = {
  duration: number;
  forcedStyleAndLayoutDuration: number;
  pauseDuration: number;
  invokerType?: string;
  sourceFunctionName?: string;
  sourceURL?: string;
};

export type LongAnimationFrameEntry = {
  startTime: number;
  duration: number;
  blockingDuration: number;
  renderStart: number;
  styleAndLayoutStart: number;
  scripts: readonly LongAnimationFrameScript[];
};

/**
 * A code identifier longer than this is dropped rather than truncated. A truncated identifier names
 * nothing, and would read as an attribution where there was only a guess.
 */
const MAX_SCRIPT_IDENTIFIER_CHARS = 128;

/** Room for any real bundle path. Past this the path is dropped, never truncated — see below. */
const MAX_SCRIPT_SOURCE_URL_CHARS = 200;

/**
 * `invokerType` is a closed spec vocabulary — `event-listener`, `user-callback`, `module-script` and
 * the like. Matching that shape rather than the value list keeps a newly specified value readable
 * while still being narrow enough that nothing data-shaped survives.
 */
const INVOKER_TYPE = /^[a-z]+(?:-[a-z]+)*$/;

/**
 * Sources we intentionally keep. Chromium is the only LoAF implementation, so
 * `chrome-extension:` is the sole extension scheme with useful attribution here.
 */
const TRUSTED_SCRIPT_URL_PROTOCOLS = new Set(["http:", "https:", "chrome-extension:"]);

/** Keep a value only where it is shaped like code. Same rationale as `sanitizeComponentStack`. */
function sanitizeScriptIdentifier(value: string | undefined, shape: RegExp): string | undefined {
  if (!value || value.length > MAX_SCRIPT_IDENTIFIER_CHARS || !shape.test(value)) {
    return undefined;
  }

  return value;
}

/**
 * Reduce a script URL to origin and pathname. The query is dropped rather than filtered — a
 * dev-server frame carries a cache-busting stamp and nothing else worth reading — and a source
 * outside the explicit scheme allowlist is dropped outright. A `chrome-extension://` origin
 * survives on purpose: an extension executing on this surface is a live candidate for the stall
 * this reading exists to attribute.
 */
export function sanitizeScriptSourceUrl(sourceUrl: string | undefined): string | undefined {
  if (!sourceUrl) {
    return undefined;
  }

  let url: URL;
  try {
    url = new URL(sourceUrl);
  } catch {
    return undefined;
  }

  if (!TRUSTED_SCRIPT_URL_PROTOCOLS.has(url.protocol)) {
    return undefined;
  }

  // `url.origin` is the literal string "null" for every non-special scheme, which is exactly the
  // `chrome-extension://` case worth keeping — so the origin is rebuilt from its parts instead.
  const origin = `${url.protocol}//${url.host}`;
  const full = `${origin}${url.pathname}`;

  // A truncated path is a path that does not exist, and it reads as one that does. Fall back to the
  // origin, which is the part that names the culprit — an extension id, or our own bundle host.
  return full.length > MAX_SCRIPT_SOURCE_URL_CHARS ? origin : full;
}

/**
 * The frame readings ride as super properties because `UserFacingErrorProperties` is closed and
 * owned elsewhere: `durationMs` is its only numeric key, and every key here would have to invent
 * one. Unlike transcript size these describe a single frame rather than the session, so
 * `captureLongAnimationFrame` clears them again immediately after the emit — a later event carrying
 * them would name the wrong code.
 *
 * `qwibLongFrameScriptSourceFunction`, not `…SourceFunctionName`: the package rejects any property
 * key ending in "name", loudly, at the call site.
 */
const LONG_FRAME_PROPERTY_KEYS = [
  "qwibLongFrameBlockingDurationMs",
  "qwibLongFrameRenderDurationMs",
  "qwibLongFrameStyleAndLayoutDurationMs",
  "qwibLongFrameScriptCount",
  "qwibLongFrameScriptsDurationMs",
  "qwibLongFrameScriptDurationMs",
  "qwibLongFrameScriptForcedStyleAndLayoutMs",
  "qwibLongFrameScriptPauseDurationMs",
  "qwibLongFrameScriptInvokerType",
  "qwibLongFrameScriptSourceFunction",
  "qwibLongFrameScriptSourceUrl",
] as const;

type LongFrameProperties = Partial<Record<(typeof LONG_FRAME_PROPERTY_KEYS)[number], number | string>>;

const CLEARED_LONG_FRAME_PROPERTIES = Object.fromEntries(LONG_FRAME_PROPERTY_KEYS.map((key) => [key, null]));

const longestScript = (scripts: readonly LongAnimationFrameScript[]): LongAnimationFrameScript | undefined =>
  scripts.reduce<LongAnimationFrameScript | undefined>(
    (longest, script) => (longest && longest.duration >= script.duration ? longest : script),
    undefined
  );

/** Only the longest script is attributed. The rest of the array is noise the frame totals cover. */
function longestScriptProperties(script: LongAnimationFrameScript): LongFrameProperties {
  return {
    qwibLongFrameScriptDurationMs: Math.round(script.duration),
    qwibLongFrameScriptForcedStyleAndLayoutMs: Math.round(script.forcedStyleAndLayoutDuration),
    qwibLongFrameScriptPauseDurationMs: Math.round(script.pauseDuration),
    qwibLongFrameScriptInvokerType: sanitizeScriptIdentifier(script.invokerType, INVOKER_TYPE),
    qwibLongFrameScriptSourceFunction: sanitizeScriptIdentifier(script.sourceFunctionName, COMPONENT_IDENTIFIER),
    qwibLongFrameScriptSourceUrl: sanitizeScriptSourceUrl(script.sourceURL),
  };
}

export function longFrameProperties(frame: LongAnimationFrameEntry): LongFrameProperties {
  const frameEnd = frame.startTime + frame.duration;
  const script = longestScript(frame.scripts);

  return {
    qwibLongFrameBlockingDurationMs: Math.round(frame.blockingDuration),
    // A phase that never ran starts at 0. Reporting it as 0ms would read as a measurement of it.
    qwibLongFrameRenderDurationMs: frame.renderStart > 0 ? Math.round(frameEnd - frame.renderStart) : undefined,
    qwibLongFrameStyleAndLayoutDurationMs:
      frame.styleAndLayoutStart > 0 ? Math.round(frameEnd - frame.styleAndLayoutStart) : undefined,
    qwibLongFrameScriptCount: frame.scripts.length,
    // The decisive number: a script total far under the frame's own duration is renderer suspension
    // or GC, not our code, and no amount of attribution on the longest script would say so.
    qwibLongFrameScriptsDurationMs: Math.round(frame.scripts.reduce((total, { duration }) => total + duration, 0)),
    ...(script ? longestScriptProperties(script) : {}),
  };
}

/**
 * A frame slow enough that the app looked frozen, with the script attribution behind it. Same
 * severity and component as `captureMainThreadStall` — the two describe one freeze from two angles,
 * and `longtask` stays as it is so the series B-2020 started remains comparable across this change.
 */
export function captureLongAnimationFrame(frame: LongAnimationFrameEntry): void {
  safePostHogCall(LONG_FRAME_REGISTER_FAILED, () => registerSuperProperties(longFrameProperties(frame)));

  captureUserFacingError(EMIT_FAILED, {
    routeName: routeName(),
    surface: UserFacingErrorSurface.Unknown,
    errorKind: UserFacingErrorKind.Timeout,
    severity: ErrorSeverity.Warning,
    component: "qwib-performance",
    operation: QwibTelemetryOperation.LongAnimationFrame,
    recoverable: true,
    durationMs: Math.round(frame.duration),
  });

  safePostHogCall(LONG_FRAME_REGISTER_FAILED, () => registerSuperProperties(CLEARED_LONG_FRAME_PROPERTIES));
}

/**
 * Transcript size as super properties, so it rides along on whatever event comes next — including a
 * crash or a stall the renderer never got to report from. An event property could only describe the
 * emit that carried it; a super property describes the state the session was in.
 *
 * `entryCount`, not `messageCount`: the package rejects any property key containing "message" as a
 * PII stem, loudly, at the call site.
 */
export function recordQwibTranscriptDimensions(dimensions: ChatTranscriptDimensions): void {
  safePostHogCall("[qwib telemetry] transcript dimensions register failed", () =>
    registerSuperProperties({
      qwibTranscriptSessionId: dimensions.sessionId,
      qwibTranscriptEntryCount: dimensions.entryCount,
      qwibTranscriptPartCount: dimensions.partCount,
      qwibTranscriptEstimatedBytes: dimensions.estimatedBytes,
    })
  );
}

/**
 * Drop the registered dimensions. Super properties are persisted by posthog-js, so without this a
 * closed session would keep attributing every later event — and every later reload — to a
 * transcript that is no longer on screen.
 */
export function clearQwibTranscriptDimensions(): void {
  safePostHogCall("[qwib telemetry] transcript dimensions clear failed", () =>
    registerSuperProperties({
      qwibTranscriptSessionId: null,
      qwibTranscriptEntryCount: null,
      qwibTranscriptPartCount: null,
      qwibTranscriptEstimatedBytes: null,
    })
  );
}
